import { useMemo, useCallback } from 'react';
import { useGetAvailabilitySlotsQuery } from '@teachur/http-client';
import {
  generateCalendarDays,
  formatMonthYear,
  getMonthBoundaries,
  addBusinessHours,
  getSlotStatus,
} from '@new/features/schedule/lib/helpers';
import { DAYS_HEADERS, BOOKING_BUFFER_HOURS, SUNDAY, SATURDAY } from '../../lib/constants';
import { CalendarDay } from './CalendarDay';
import { CalendarNavigationButton } from './CalendarNavigationButton';

interface CalendarAvailableDatesPickerProps {
  selectedMonth: Date;
  selectedDay: Date;
  setSelectedMonth: (date: Date) => void;
  setSelectedDay: (date: Date) => void;
  courseId: number;
}

export const CalendarAvailableDatesPicker = ({
  selectedMonth,
  selectedDay,
  setSelectedMonth,
  setSelectedDay,
  courseId,
}: CalendarAvailableDatesPickerProps) => {
  const currentMonth = formatMonthYear(selectedMonth);
  const today = useMemo(() => new Date(), []);
  const year = selectedMonth.getFullYear();
  const month = selectedMonth.getMonth();

  const { monthStart, monthEnd } = useMemo(() => getMonthBoundaries(year, month), [year, month]);

  const { data: monthAvailableSlots } = useGetAvailabilitySlotsQuery({
    startDate: monthStart.toISOString(),
    endDate: monthEnd.toISOString(),
    courseId,
    availableOnly: true,
  });

  const datesWithSlots = useMemo(() => {
    const dateSet = new Set<string>();
    if (!monthAvailableSlots) return dateSet;
    const earliestBookable = addBusinessHours(today, BOOKING_BUFFER_HOURS);
    for (const slot of monthAvailableSlots) {
      if (getSlotStatus(slot) !== 'available') continue;

      const slotDate = new Date(slot.start);
      if (slotDate < earliestBookable) continue;
      const dayOfWeek = slotDate.getDay();
      if (dayOfWeek === SUNDAY || dayOfWeek === SATURDAY) continue;
      const dateKey = `${slotDate.getFullYear()}-${slotDate.getMonth()}-${slotDate.getDate()}`;
      if (!dateSet.has(dateKey)) {
        dateSet.add(dateKey);
      }
    }
    return dateSet;
  }, [monthAvailableSlots, today]);

  const hasSlots = useCallback(
    (date: Date | null): boolean => {
      if (!date) return false;
      const dateKey = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
      return datesWithSlots.has(dateKey);
    },
    [datesWithSlots]
  );

  const days = useMemo(() => generateCalendarDays(year, month), [year, month]);

  const handleMonthChange = useCallback(
    (direction: 'prev' | 'next') => {
      const MONTH_DECREMENT = -1;
      const MONTH_INCREMENT = 1;
      const FIRST_DAY_OF_MONTH = 1;

      const monthOffset = direction === 'prev' ? MONTH_DECREMENT : MONTH_INCREMENT;
      const newMonth = new Date(year, month + monthOffset, FIRST_DAY_OF_MONTH);
      setSelectedMonth(newMonth);
      setSelectedDay(newMonth);
    },
    [year, month, setSelectedMonth, setSelectedDay]
  );

  const handleDayClick = useCallback(
    (date: Date) => {
      setSelectedDay(date);
    },
    [setSelectedDay]
  );

  return (
    <div className="flex-1 flex flex-col gap-5">
      <div className="flex items-center justify-between">
        <CalendarNavigationButton direction="prev" onClick={() => handleMonthChange('prev')} />
        <h3 className="[font-size:var(--fs-l4)]! lg:[font-size:var(--fs-l2)]! [font-family:var(--font-montserrat)]! text-(--neutral-black-NB1000)! font-semibold m-0!">
          {currentMonth}
        </h3>
        <CalendarNavigationButton direction="next" onClick={() => handleMonthChange('next')} />
      </div>

      <div className="grid grid-cols-7 gap-2 text-center [font-size:var(--fs-l5)]! text-(--neutral-black-NB900)!">
        {DAYS_HEADERS.map((day, idx) => (
          <div key={idx}>{day}</div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-2 lg:gap-1">
        {days.map((day, idx) => (
          <CalendarDay
            key={idx}
            date={day.date}
            dayNumber={day.dayNumber}
            selectedDay={selectedDay}
            today={today}
            hasSlots={hasSlots}
            onClick={handleDayClick}
          />
        ))}
      </div>
    </div>
  );
};
